import { WarningIcon } from "@phosphor-icons/react/Warning";
import { Badge } from "@freightsimple/ui-core/Display/Badge";
import { Panel } from "@freightsimple/ui-core/PageLayout/Panel";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import {
  FormItem,
  type ErrorMessageType,
} from "@freightsimple/ui-core/FormLayout/FormItem";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Link } from "@freightsimple/ui-core/Typography/Link";
import { NumberInput } from "@freightsimple/ui-core/Controls/NumberInput";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Switch } from "@freightsimple/ui-core/Controls/Switch";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Tooltip } from "@freightsimple/ui-core/Feedback/Tooltip";

import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import BookShipmentSection from "../CustomComponents/BookShipmentSection";
import { BookShipmentInsuranceQuestionBubble } from "../Bubbles/BookShipmentInsuranceQuestionBubble";
import { BookShipmentStateOfGoodsSelector } from "../CustomComponents/BookShipmentStateOfGoodsSelector";
import { BookShipmentInsuranceReveal } from "../CustomComponents/Internal/bookShipmentInsuranceReveal";

interface InsurancePanelProps {
  addInsuranceToShipment: boolean;
  onAddInsuranceToShipmentChanged: (newValue: boolean) => void;

  insuranceAmount: number | undefined;
  onSetInsuranceAmount: (newValue: number) => void;

  insuranceCurrency: Currency;
  onSetInsuranceCurrency: (newValue: Currency) => void;

  locationsErrorMessageForInsurance: ErrorMessageType;
  forceValidation: boolean;

  stateOfGoods?: StateOfGoods | undefined;
  setStateOfGoods: (newValue: StateOfGoods) => void;
  stateOfGoodsErrorMessages: ErrorMessageType;

  index: number;
}

const currencyOptions = [
  { value: Currency.Cad, label: "CAD" },
  { value: Currency.Usd, label: "USD" },
];

function statedValueHelpContent() {
  return (
    <Stack gap={2}>
      <T weight="medium">Commercial Invoice Stated Value</T>
      <T>
        Enter the total value of your goods from your commercial invoice
        (cross-border) or purchase order/sales invoice (domestic). This value
        must match your documentation to ensure valid insurance claims.
      </T>
      <Link
        href="https://help.freightsimple.com/using-freightsimple/quoting/insurance"
        external
      >
        Learn more
      </Link>
    </Stack>
  );
}

function conditionHelpContent() {
  return (
    <Stack gap={2}>
      <T weight="medium">Condition</T>
      <T>
        Specify if goods are new or used. This affects insurance coverage — new
        items get full replacement value while used items may have depreciated
        coverage or limits applied during claims. Select accurately for proper
        coverage expectations.
      </T>
      <Link
        href="https://help.freightsimple.com/using-freightsimple/quoting/insurance"
        external
      >
        Learn more
      </Link>
    </Stack>
  );
}

export function BookShipmentInsurancePanel(props: InsurancePanelProps) {
  return (
    <BookShipmentSection
      id="shipment-insurance"
      index={props.index}
      width="narrow"
      header={
        <>
          Insurance <Badge label="Optional" variant="neutral" />
        </>
      }
      description={
        <>
          Protect your freight with additional insurance coverage
          <BookShipmentInsuranceQuestionBubble />
        </>
      }
    >
      <Panel collapseOnNarrow>
        {/* gap 0: the reveal carries the space above its own fields, so the
            collapsed panel sits evenly between its top and bottom padding. */}
        <Stack gap={0} align="center">
          <Row gap={2} align="center">
            <T size="lg" color="secondary">
              Add insurance to this shipment?
            </T>
            <Switch
              size="large"
              checked={props.addInsuranceToShipment}
              onChange={props.onAddInsuranceToShipmentChanged}
            />
          </Row>

          <BookShipmentInsuranceReveal open={props.addInsuranceToShipment}>
            <ShowFormErrors show={props.forceValidation}>
              <Stack gap={4} align="center">
                <Stack gap={2} align="center">
                  <T weight="bold">
                    Enter Commercial Invoice Stated Value
                    <Tooltip
                      variant="info"
                      content={statedValueHelpContent()}
                    />
                  </T>
                  <Row gap={1} align="center">
                    <Icon
                      icon={WarningIcon}
                      weight="fill"
                      size="sm"
                      color="text-orange"
                    />
                    <T size="xs" color="muted">
                      Insurance invalid if value does not match your commercial
                      invoice
                    </T>
                  </Row>
                </Stack>

                <Row gap={4} align="start">
                  <FormItem error={props.locationsErrorMessageForInsurance}>
                    <NumberInput
                      value={props.insuranceAmount}
                      setValue={props.onSetInsuranceAmount}
                      placeholder="Value"
                      startAdornment="$"
                      width="sm"
                      disabled={!props.addInsuranceToShipment}
                      data-testid="insurance-amount"
                    />
                  </FormItem>
                  <Dropdown
                    value={props.insuranceCurrency}
                    onChange={(v) =>
                      props.onSetInsuranceCurrency(v as Currency)
                    }
                    options={currencyOptions}
                    placeholder="Select"
                    width="xs"
                    disabled={!props.addInsuranceToShipment}
                    data-testid="insurance-currency"
                  />
                </Row>

                <T weight="bold">
                  Select Condition
                  <Tooltip variant="info" content={conditionHelpContent()} />
                </T>

                <BookShipmentStateOfGoodsSelector
                  stateOfGoods={props.stateOfGoods}
                  onStateOfGoodsChange={(value) => props.setStateOfGoods(value)}
                  disabled={!props.addInsuranceToShipment}
                  forceValidation={props.forceValidation}
                  errorMessage={props.stateOfGoodsErrorMessages}
                />
              </Stack>
            </ShowFormErrors>
          </BookShipmentInsuranceReveal>
        </Stack>
      </Panel>
    </BookShipmentSection>
  );
}
